import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, MessageCircle } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const CTASection = ({
  title = "Trasforma il tuo spazio esterno",
  description = "Richiedi una consulenza gratuita e scopri la soluzione perfetta per te.",
  ctaText = "Richiedi un preventivo personalizzato",
  ctaLink = "/preventivo",
  variant = 'light'
}) => {
  const isLight = variant === 'light';

  return (
    <section className={`relative py-24 lg:py-32 overflow-hidden ${
      isLight ? 'bg-gradient-to-br from-sky-50 via-white to-blue-50' : 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
    }`}>
      {/* Decorative elements */}
      <div className={`absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20 ${
        isLight ? 'bg-sky-400' : 'bg-sky-500'
      }`} />
      <div className={`absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl opacity-20 ${
        isLight ? 'bg-blue-400' : 'bg-blue-600'
      }`} />
      
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${isLight ? '0ea5e9' : 'ffffff'}' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 ${
            isLight ? 'bg-sky-100 text-sky-700' : 'bg-white/10 text-sky-300'
          }`}>
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                isLight ? 'bg-sky-500' : 'bg-sky-400'
              }`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${
                isLight ? 'bg-sky-600' : 'bg-sky-400'
              }`}></span>
            </span>
            <span className="text-sm font-medium">Preventivo gratuito in 24h</span>
          </div>
          
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-6 ${
            isLight ? 'text-gray-900' : 'text-white'
          }`}>
            {title}
          </h2>
          <p className={`text-lg md:text-xl font-light mb-10 max-w-2xl mx-auto ${
            isLight ? 'text-gray-600' : 'text-gray-300'
          }`}>
            {description}
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link to={ctaLink} className="group">
              <button 
                className={`relative px-8 py-4 text-lg font-medium transition-all duration-300 overflow-hidden ${
                  isLight 
                    ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/30 hover:shadow-xl hover:shadow-sky-500/40' 
                    : 'bg-white text-gray-900 shadow-lg shadow-white/20 hover:shadow-xl hover:shadow-white/30'
                }`}
              >
                {/* Shine effect */}
                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                
                <span className="relative flex items-center gap-2">
                  {ctaText}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </button>
            </Link>
            
            <a 
              href={`tel:${companyInfo?.phone || '050878189'}`}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-all duration-300 border-2 ${
                isLight 
                  ? 'border-gray-300 text-gray-700 hover:border-sky-500 hover:text-sky-600 hover:bg-sky-50' 
                  : 'border-white/30 text-white hover:border-white hover:bg-white/10'
              }`}
            >
              <Phone className="w-5 h-5" />
              Chiamaci ora
            </a>
          </div>
          
          {/* Trust indicators */}
          <div className={`flex flex-wrap items-center justify-center gap-6 text-sm ${
            isLight ? 'text-gray-500' : 'text-gray-400'
          }`}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Sopralluogo gratuito</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Preventivo senza impegno</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Installatori interni</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
